/* NFC Solutions — Change Event Envelope
 * -----------------------------------------------------------------
 * Every offline-capable mutation is modelled as an EVENT rather than
 * a database overwrite. Session 25 defines the envelope + helpers.
 * Session 26 will wire this into the write path (local queue + sync
 * engine) and the future Local Coordinator will simply relay these
 * events across the LAN.
 *
 * Design principles:
 *  • Client-generated `change_id` — device is the source of truth
 *    for identity so the server can dedupe on reconnect.
 *  • Every event carries its context: device, business, user, ts.
 *  • Payloads are event-specific but self-contained (no external
 *    references beyond IDs already present locally).
 *  • Immutable — never mutate an event after creation; replace with
 *    a new event that references the original via `parent_change_id`.
 */

import { getDeviceId } from "./device";

// Canonical event types. Adding one? Update the server-side switch
// too when Session 26 wires the /api/sync/apply endpoint.
export const EventType = Object.freeze({
  // Session lifecycle
  TABLE_OPENED: "table_opened",
  TABLE_CLOSED: "table_closed",
  TABLE_SESSION_MOVED: "table_session_moved",

  // Order lifecycle
  ORDER_CREATED: "order_created",
  ORDER_UPDATED: "order_updated",
  ORDER_STATUS_CHANGED: "order_status_changed",
  ITEM_ADDED: "item_added",
  ITEM_REMOVED: "item_removed",
  ITEM_QUANTITY_CHANGED: "item_quantity_changed",

  // Payment lifecycle
  PAYMENT_RECORDED: "payment_recorded",
  // Session 31.26 — APPEND one or more tenders (cash / card / eft, each
  // with its own tip + label) to the table's multi-tender ledger. Never
  // overwrites earlier tenders, so partial payments from two devices
  // converge after sync.
  PAYMENT_ADDED: "payment_added",
  PAYMENT_EDITED: "payment_edited",
  BILL_SPLIT: "bill_split",
  BILL_SPLIT_CLEARED: "bill_split_cleared",
  SPLIT_PAYMENT_PROCESSED: "split_payment_processed",

  // Voucher / discount
  VOUCHER_APPLIED: "voucher_applied",
  VOUCHER_REMOVED: "voucher_removed",

  // Waiter / service
  WAITER_CALLED: "waiter_called",
  CALL_ACKNOWLEDGED: "call_acknowledged",

  // Configuration (owner-side; queued when offline)
  PRODUCT_UPDATED: "product_updated",
  MENU_ITEM_UPDATED: "menu_item_updated",
  MENU_ITEM_CREATED: "menu_item_created",
  MENU_ITEM_DELETED: "menu_item_deleted",
  CATEGORY_UPDATED: "category_updated",
  CATEGORY_CREATED: "category_created",
  CATEGORY_DELETED: "category_deleted",

  // Session 40 — stock-affecting actions that must survive offline.
  ORDER_VOIDED: "order_voided",
  STOCK_WASTE_RECORDED: "stock_waste_recorded",
  STOCK_RECEIVED: "stock_received",
  STOCK_ADJUSTED: "stock_adjusted",
  STOCK_TRANSFERRED: "stock_transferred",
  STOCK_TAKE_APPLIED: "stock_take_applied",

  // Session 41 — prep execution (kitchen / bar) + production confirm.
  // Prep progress / submit never move stock; production_confirmed is the
  // ONLY prep event with a ledger effect (PRODUCTION, never TRANSFER).
  PREP_PROGRESS_RECORDED: "prep_progress_recorded",
  PREP_SHEET_SUBMITTED: "prep_sheet_submitted",
  PRODUCTION_CONFIRMED: "production_confirmed",
});

export const STOCK_EVENT_TYPES = Object.freeze([
  "order_voided", "stock_waste_recorded", "stock_received", "stock_adjusted", "stock_transferred", "stock_take_applied",
  "production_confirmed", "prep_progress_recorded", "prep_sheet_submitted",
]);

const ALL_EVENT_TYPES = new Set(Object.values(EventType));

/**
 * Local Event Engine lifecycle. Every event moves through these states
 * in order. Session 25.5 defines the enum; Session 26 wires the queue
 * that drives transitions.
 *
 *   LOCAL      — freshly created on this device, written to local DB
 *   BROADCAST  — replicated over the LAN (Local Coordinator, Session 26+)
 *   PENDING    — cloud sync attempted but internet unavailable
 *   SYNCED     — accepted by the cloud (server has acknowledged)
 *   FAILED     — permanently rejected by the cloud (needs manual resolution)
 */
export const EventLifecycle = Object.freeze({
  LOCAL: "LOCAL",
  BROADCAST: "BROADCAST",
  PENDING: "PENDING",
  SYNCED: "SYNCED",
  FAILED: "FAILED",
});

const ALL_LIFECYCLES = new Set(Object.values(EventLifecycle));

export function isKnownLifecycle(l) {
  return ALL_LIFECYCLES.has(String(l || ""));
}

/** Fast v4 UUID — same algorithm as crypto.randomUUID but with a
 * fallback for older mobile WebViews. */
export function uuidV4() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_) { /* fall through */ }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Build a change envelope for an offline-capable mutation.
 *
 * @param {string} eventType — one of EventType.* values
 * @param {object} payload   — event-specific data
 * @param {object} ctx       — { session, deviceId? }
 * @returns {object} envelope ready to be queued for sync
 */
export function createChangeEnvelope(eventType, payload, ctx = {}) {
  if (!ALL_EVENT_TYPES.has(eventType)) {
    // Non-fatal — allow forward-compat event types, but flag them so the
    // sync engine can reject unknown types cleanly on the server side.
    console.warn(`[events] unknown event type "${eventType}" — will be sent as-is`);
  }
  const session = ctx.session || {};
  const business = session.business || {};
  const user = session.user || session.staff || {};
  return {
    change_id: uuidV4(),
    device_id: ctx.deviceId || (() => { try { return getDeviceId(); } catch (_) { return null; } })(),
    business_id: business.id || business.slug || null,
    business_slug: business.slug || null,
    user_id: user.id || null,
    user_name: user.full_name || user.name || user.username || null,
    role: session.role || null,
    event_type: eventType,
    ts: new Date().toISOString(),
    parent_change_id: null,      // set when this event supersedes another
    payload: payload || {},
    // Metadata for the sync engine — filled by the queue layer in
    // Session 26. Kept here for shape completeness. `status` uses the
    // EventLifecycle enum: LOCAL → BROADCAST → PENDING → SYNCED.
    _meta: {
      status: EventLifecycle.LOCAL,
      attempts: 0,
      last_error: null,
      broadcast_at: null,
      synced_at: null,
    },
  };
}

/**
 * Convenience factory that returns a bound helper for a given session,
 * so callers don't repeat `{ session }` on every call.
 *
 *   const emit = eventEmitterFor(session);
 *   const evt = emit(EventType.ITEM_ADDED, { order_id, item_id });
 */
export function eventEmitterFor(session) {
  return (eventType, payload) => createChangeEnvelope(eventType, payload, { session });
}

/** Type-guard: is this a known event type? */
export function isKnownEventType(t) {
  return ALL_EVENT_TYPES.has(String(t || ""));
}
